// ===========================================================================
// System notices.
//
// A notice from Lumi itself: a moderation warning, a restart, an
// administrator's announcement. Three things make one different from a message
// somebody typed.
//
// It has a LEVEL, chosen by whoever sent it -- red for something wrong, amber
// for something to watch, green for good news, grey by default -- so the
// weight of it is legible before a word is read.
//
// It PINS. A busy text room can scroll a notice away in seconds, which is
// exactly when a notice matters most, so once it would leave the top of the
// transcript it sticks there for a while and then lets go.
//
// And it CANNOT BE FAKED. Nothing a user types reaches this code. The
// transcript used to decide a line was a system notice by comparing the
// sender's handle to the string 'system' -- and 'system' was an allowed handle,
// so anyone could take that name and post what looked like an official notice.
// Now system-ness is structural: notices are built here, by this function, from
// a server message that no client can send, and a chat line has no path into
// it whatever it is called. The handle is refused as well, but that is the
// second lock, not the first.
// ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';
import { linkifyNodes } from './linkify.js';
import { SYSTEM_PIN_MS, cleanSystemLevel } from '@lumi/shared';

let seq = 0;

/**
 * Add a system notice to a transcript, and pin it when it scrolls off the top.
 *
 * @param {HTMLElement} messages the scrolling transcript
 * @param {HTMLElement} pinHost  where a pinned copy lives (above the transcript)
 * @param {string} text
 * @param {{ level?: string, id?: string }} opts
 */
/**
 * Notices that can be taken back, by id.
 *
 * A notice usually stands as a record of something that happened and stays
 * put. But some of them describe a state rather than an event -- "this room
 * has a video pinned" is only true while there is one -- and leaving that
 * behind after the video is unpinned tells people something false about the
 * room they are in.
 *
 * The pinned copy lives in a closure, so it cannot be found from the DOM;
 * this is how both halves get cleared together.
 */
const revocable = new Map();

/** Take back a notice added with a known id. Safe to call for one that is
 *  already gone -- callers should not have to track that. */
export function removeSystemNotice(id) {
  const hit = revocable.get(String(id));
  if (!hit) return false;
  revocable.delete(String(id));
  hit.node.remove();
  hit.unpin?.();
  return true;
}

export function addSystemNotice(messages, pinHost, text, opts = {}) {
  // Never trusted: a level arrives over the wire and becomes a class name.
  const level = cleanSystemLevel(opts.level);
  // Only needs to be unique in this browser -- it keys the pinned copy to the
  // line in the transcript, nothing more.
  const id = String(opts.id || `sys_local_${(seq += 1)}`);

  // One optional action, for a notice somebody can DO something about. The
  // question of the hour is the case that needs it: it is announced here as
  // well as pinned in the rules card, and the card is often collapsed, so for
  // most people this line IS the question and it had nothing to answer with.
  const action = opts.action
    ? el('button', {
        class: 'btn btn-ghost btn-xs sys-action',
        type: 'button',
        text: opts.action.text,
        title: opts.action.title || opts.action.text,
        onClick: () => opts.action.onClick?.(),
      })
    : null;
  // Every notice can be sent away. They pile up -- the rules, the question,
  // a playlist landing, somebody arriving -- and in a busy room they end up
  // between a person and the conversation they came for. Dismissing one
  // dismisses ONLY it: they are separate things to have been told.
  let unpin = null;
  const close = el('button', {
    class: 'sys-close',
    type: 'button',
    title: t('Dismiss this'),
    aria: { label: 'Dismiss this' },
    text: '\u00d7', // multiplication sign, kept escaped so the source is ASCII
    onClick: () => {
      unpin?.();
      node.remove();
      revocable.delete(id);
    },
  });
  const node = el('div', {
    class: `msg system sys-${level}`,
    dataset: { sysId: id },
    role: 'status',
  }, [el('span', { class: 'sys-text' }, linkifyNodes(text, { external: true })), action, close].filter(Boolean));
  messages.append(node);
  messages.scrollTop = messages.scrollHeight;

  unpin = pinHost ? pinWhenItLeaves(messages, pinHost, node, text, level, id)?.dismiss : null;
  if (opts.id) revocable.set(id, { node, unpin });
  return node;
}

/**
 * Watch one notice, and pin a copy the moment it scrolls out of the top.
 *
 * Pinning on the way OUT rather than on arrival is deliberate: while you can
 * still see the real one there is nothing to solve, and a banner over a notice
 * already on screen is just clutter.
 */
function pinWhenItLeaves(messages, pinHost, node, text, level, id) {
  // Returns a function that removes the pinned copy and stops watching.
  let pinned = null;
  let timer = null;
  let done = false;
  let observer = null;

  const finish = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    messages.removeEventListener('scroll', onScroll);
    observer?.disconnect();
    if (pinned) {
      pinned.remove();
      pinned = null;
    }
  };

  const dismiss = () => {
    // Dismissing THIS notice, and only this one. Others stay: they are separate
    // things to have been told, and clearing one should not clear the rest.
    finish();
  };



  function scrolledOffTop() {
    // Compared as rectangles on screen, not offsetTop against scrollTop:
    // offsetTop is measured from the offsetParent, which is whatever ancestor
    // happens to be positioned -- not necessarily the transcript -- so that
    // arithmetic was quietly comparing two different coordinate spaces and
    // never became true.
    const n = node.getBoundingClientRect();
    const m = messages.getBoundingClientRect();
    return n.bottom < m.top;
  }

  function pin() {
    if (pinned || done) return;
    const close = el('button', {
      class: 'sys-pin-close',
      type: 'button',
      title: t('Dismiss this notice'),
      aria: { label: 'Dismiss this notice' },
      text: '\u00d7', // multiplication sign, escaped to keep the source ASCII
      onClick: dismiss,
    });
    pinned = el('div', { class: `sys-pin sys-${level}`, dataset: { sysId: id }, role: 'status' }, [
      el('span', { class: 'sys-text' }, linkifyNodes(text, { external: true })),
      close,
    ]);
    pinHost.append(pinned);
    // Let go on its own, so a notice nobody dismisses does not sit there for
    // the rest of the conversation.
    timer = setTimeout(finish, SYSTEM_PIN_MS);
  }

  function onScroll() {
    if (done) return;
    if (scrolledOffTop()) pin();
  }

  messages.addEventListener('scroll', onScroll, { passive: true });
  // Messages arriving beneath it push it off the top without any scroll of the
  // user's own, so watch the list for additions too.
  observer = typeof MutationObserver === 'function' ? new MutationObserver(onScroll) : null;
  observer?.observe(messages, { childList: true });
  return { dismiss, stop: finish };
}

export default { addSystemNotice };
